import 'client-only';

import type React from 'react';

import { type Brand } from '@/consts/brands';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { ProductSelectionAndCheckoutService } from '@/services/productSelectionAndCheckoutService/client';
import type { ShippingAddressFields } from '@/types/productSelectionAndCheckout/types';
import { type PurchaseContext } from '@/types/purchaseContextToggle/types';
import { get, put } from '@/utils/http/client';
import { urls } from '@/utils/urls';

import {
  type PreviousResponsesData,
  type Patient,
  type ResubscriptionFormulationData,
} from './types';

const getResubscriptionFormulationData = (brand: Brand) =>
  get<{ data: ResubscriptionFormulationData }>(
    urls[brand].api.resubscription.data.formulation,
  );

const getPreviousResponses = (brand: Brand) =>
  get<{ data: PreviousResponsesData }>(
    urls[brand].api.resubscription.previousResponses,
  );

const resubscribeUser = (
  brand: Brand,
  treatmentCategory: TreatmentCategory,
  purchaseContext: PurchaseContext,
) =>
  put(urls[brand].api.resubscription.resubscribe, {
    treatmentCategory,
    surveyProvider: 'surveyjs',
    purchaseContext,
  });

const updateShippingAddress = async (
  brand: Brand,
  setPatient: React.Dispatch<React.SetStateAction<Patient>>,
  shippingAddressFields: ShippingAddressFields,
  onFailure: () => void,
) => {
  try {
    const data = {
      address1: shippingAddressFields.streetAddress,
      address2: shippingAddressFields.apartmentSuite,
      addressee_name: `${shippingAddressFields.firstName} ${shippingAddressFields.lastName}`,
      city: shippingAddressFields.city,
      firstname: shippingAddressFields.firstName,
      lastname: shippingAddressFields.lastName,
      phoneNumberOptional: true,
      state: shippingAddressFields.state,
      zipcode: shippingAddressFields.zipCode,
    };

    const {
      data: { user },
    } = await ProductSelectionAndCheckoutService.updateShippingAddress(
      brand,
      data,
    );

    setPatient({
      firstname: user.firstname,
      lastname: user.lastname,
      address1: user.address1 || '',
      address2: user.address2 || '',
      city: user.city || '',
      state: user.state || '',
      zipcode: user.zipcode || '',
    });

    return true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (exception: any) {
    onFailure();
    return false;
  }
};

export const ResubscriptionService = {
  getPreviousResponses,
  getResubscriptionFormulationData,
  resubscribeUser,
  updateShippingAddress,
};
